import React, { useMemo } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import type { ProjectMember } from '../types/project';
import {
  memberOptionLabel,
  selectedMemberLabel,
  toggleEmployeeId,
} from '../utils/projectTaskForm';

type Props = {
  label: string;
  members: ProjectMember[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  error?: string;
  accessibilityLabel?: string;
  /** Inline rows (subtask + member) hide the caption; the field keeps its label for screen readers. */
  showLabel?: boolean;
};

export function ProjectMemberPicker({
  label,
  members,
  selectedIds,
  onChange,
  error,
  accessibilityLabel = 'Assign members',
  showLabel = true,
}: Props) {
  const palette = useAppColors();
  const [open, setOpen] = React.useState(false);
  const { height: windowHeight } = useWindowDimensions();
  // Cap the list so it scrolls on its own instead of stretching the parent form.
  const listMaxHeight = Math.max(
    160,
    Math.min(320, Math.round(windowHeight * 0.4)),
  );
  const themed = useMemo(
    () => ({
      label: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      value: { ...typography.input, color: palette.textPrimary },
      placeholder: { ...typography.input, color: palette.textMuted },
      error: { ...typography.caption, color: palette.error },
      memberName: { ...typography.body, color: palette.textPrimary },
      memberMeta: { ...typography.caption, color: palette.textSecondary },
      field: {
        borderWidth: 1,
        borderColor: error ? palette.error : palette.border,
        backgroundColor: palette.surface,
        borderRadius: 10,
        minHeight: 44,
        paddingHorizontal: spacing.md,
        justifyContent: 'center' as const,
      },
    }),
    [error, palette],
  );

  const selected = selectedMemberLabel(members, selectedIds);

  // Each pick closes the list; tapping the field again reopens it.
  function choose(ids: number[]) {
    setOpen(false);
    onChange(ids);
  }

  return (
    <View>
      {showLabel ? <Text style={themed.label}>{label}</Text> : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ expanded: open }}
        onPress={() => setOpen(current => !current)}
        style={themed.field}
      >
        <Text
          numberOfLines={2}
          style={selectedIds.length ? themed.value : themed.placeholder}
        >
          {selected}
        </Text>
      </Pressable>
      {error ? <Text style={themed.error}>{error}</Text> : null}
      {open ? (
        <View
          style={[
            styles.memberList,
            { borderColor: palette.border, backgroundColor: palette.surface },
          ]}
        >
          <ScrollView
            style={[
              { maxHeight: listMaxHeight },
              Platform.OS === 'web' ? ({ overflow: 'auto' } as object) : null,
            ]}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear members"
              onPress={() => choose([])}
              style={styles.memberRow}
            >
              <Text style={themed.memberName}>Unassigned</Text>
            </Pressable>
            {members.map(member => {
              const selectedMember = selectedIds.includes(member.employeeId);
              return (
                <Pressable
                  key={member.employeeId}
                  accessibilityRole="button"
                  accessibilityLabel={`${
                    selectedMember ? 'Remove' : 'Add'
                  } ${memberOptionLabel(member)}`}
                  onPress={() =>
                    choose(toggleEmployeeId(selectedIds, member.employeeId))
                  }
                  style={styles.memberRow}
                >
                  <Text style={themed.memberName}>
                    {selectedMember ? '✓ ' : ''}
                    {member.employeeName}
                  </Text>
                  <Text style={themed.memberMeta}>
                    {memberOptionLabel(member)}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  memberList: {
    borderWidth: 1,
    borderRadius: 10,
    overflow: 'hidden',
    marginTop: spacing.xs,
  },
  memberRow: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    gap: 2,
  },
});
